import React from 'react';
import { ModuleLandingGrid, ModuleShortcutCard } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * More — lower-frequency HR destinations, out of the daily workflow but
 * one click away. (Round: HR UI Simplification, Phase 1.)
 *
 *   Overview (analytics) → the EXISTING /hr/dashboard page, untouched:
 *       headcount + drill-down, data quality, birthdays, anniversaries,
 *       new hires. It no longer competes as a second HR home and its
 *       widgets no longer load on the landing — they load only when
 *       someone opens Overview.
 *   Audit trail → embedded AuditTrail page. The card obeys the same
 *       server allowlist check (audit/access) the old landing used; the
 *       API enforces the same gate on the data itself.
 *   Web forms → the existing HR021 / 90-day / HR020 form tabs
 *       (?section=webforms renders them inside the shell).
 *
 * Departments and Roles administration lives under Employees →
 * Departments / Roles (single home — no duplicate links here).
 */
const LazyAudit = React.lazy(() => import('../AuditTrail'));
// Round REMIND-1 (2026-10-03): switches for the quiet in-dashboard reminders.
const LazyReminders = React.lazy(() => import('../ReminderSettings'));

const HrMoreSection = ({ item, auditAllowed, onOpenItem }) => {
    if (item === 'reminders') {
        return (
            <div>
                <button type="button" className="btn btn-light btn-sm mb-2" onClick={() => onOpenItem(null)}>
                    <i className="ri-arrow-left-line align-bottom me-1" aria-hidden="true" />Back to More
                </button>
                <div className="hr-section-title">
                    <div>
                        <h5>Reminders</h5>
                        <span className="text-muted">Quiet notes in the dashboard: approvals waiting, no clock-in yet.</span>
                    </div>
                </div>
                <HrEmbeddedPage><LazyReminders /></HrEmbeddedPage>
            </div>
        );
    }

    if (item === 'audit') {
        return (
            <div>
                <button type="button" className="btn btn-light btn-sm mb-2" onClick={() => onOpenItem(null)}>
                    <i className="ri-arrow-left-line align-bottom me-1" aria-hidden="true" />Back to More
                </button>
                <HrEmbeddedPage><LazyAudit /></HrEmbeddedPage>
            </div>
        );
    }

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>More</h5>
                    <span className="text-muted">Analytics, audit, reminders, and web forms — out of the daily path, one click away.</span>
                </div>
            </div>
            <ModuleLandingGrid title="" icon="" description="">
                <ModuleShortcutCard
                    icon="ri-dashboard-3-line"
                    label="Overview (analytics)"
                    subtitle="Headcount, data quality, birthdays, anniversaries, new hires"
                    tone="primary"
                    to="/hr/dashboard"
                />
                {auditAllowed && (
                    <ModuleShortcutCard
                        icon="ri-shield-check-line"
                        label="Audit trail"
                        subtitle="Who did what to whom — searchable HR action log"
                        tone="primary"
                        onClick={() => onOpenItem('audit')}
                    />
                )}
                <ModuleShortcutCard
                    icon="ri-notification-3-line"
                    label="Reminders"
                    subtitle="Quiet once-a-day notes: approvals waiting, no clock-in yet"
                    tone="primary"
                    onClick={() => onOpenItem('reminders')}
                />
                <ModuleShortcutCard
                    icon="ri-file-list-3-line"
                    label="Web forms"
                    subtitle="HR021 exempt review · 90-day review · HR020 PIC form"
                    tone="primary"
                    to="/humanresource?section=webforms"
                />
            </ModuleLandingGrid>
        </div>
    );
};

export default HrMoreSection;
